import * as React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { useTranslation } from 'react-i18next'
import { SxProps } from '@mui/material'
import { uniq } from 'lodash'

import {
  addFilterWithValue,
  toggleFilterByEmailDialog as closeModal,
  updateFilter,
} from 'redux/slices/FilterBuilder'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import ChipInput from 'components/Participants/ChipInput'
import { emailState as mapFilters } from 'components/Participants/Filters/FilterState'
import { dialogActionsStyle } from 'components/Participants/Filters/Styles'

type FieldNames = 'emailContains' | 'emailDoesNotContains'

type InitialValueState = {
  [key: string]: any
}

type InitialInputValueState = {
  emailContains: string
  emailDoesNotContains: string
}

const dialogStyles: SxProps = {
  sx: {
    minWidth: {
      xs: 320,
      md: 600,
    },
    height: {
      xs: 568,
      md: 394,
    },
  },
}

const initialValueState: InitialValueState = {
  emailContains: [],
  emailDoesNotContains: [],
}

const initialInputValueState: InitialInputValueState = {
  emailContains: '',
  emailDoesNotContains: '',
}

const FilterByEmail = () => {
  const { selectedFilters, showEmailDialog: open } = useAppSelector(
    (state) => state.filterBuilder,
  )

  const globalDispatch = useAppDispatch()
  const { t } = useTranslation()

  const [value, setValue] = React.useState(initialValueState)

  const [inputValue, setInputValue] = React.useState(initialInputValueState)

  const handleMapState = () => {
    /**
     * ⚠️ This syncs the slice state
     * and the local state.
     */
    mapFilters.forEach((filter) => {
      const existingFilter = selectedFilters.find(
        (selectedFilter) => selectedFilter.type === filter.type,
      )

      if (existingFilter && existingFilter.value) {
        const transformedValue = existingFilter.value.split(',')

        handleSetValue({
          fieldName: filter.type as FieldNames,
          value: transformedValue,
        })
      }
    })
  }

  React.useEffect(() => {
    if (open) {
      handleMapState()
    }
  }, [open])

  const handelClose = () => {
    setValue(initialValueState)
    setInputValue(initialInputValueState)

    globalDispatch(closeModal())
  }

  const handleApply = () => {
    mapFilters.forEach((filter) => {
      const existingFilterIndex = selectedFilters.findIndex(
        (selectedFilter) => selectedFilter.type === filter.type,
      )

      const selectedValue = value[filter.type].join()
      const filterComponent = selectedValue ? 'Email' : ''

      if (existingFilterIndex !== -1) {
        globalDispatch(
          updateFilter({
            item: {
              ...filter,
              value: selectedValue,
              component: filterComponent,
            },
            index: existingFilterIndex,
          }),
        )
      } else {
        globalDispatch(
          addFilterWithValue({
            ...filter,
            value: selectedValue,
            component: filterComponent,
          }),
        )
      }
    })

    handelClose()
  }

  const handleSetValue = ({
    value,
    fieldName,
  }: {
    value: string[]
    fieldName: FieldNames
  }) => {
    const uniqueOptions = uniq(value) as string[]

    setValue((prevValue) => ({
      ...prevValue,
      [fieldName]: uniqueOptions,
    }))
  }

  const handleSetInputValue = ({
    value,
    fieldName,
  }: {
    value: string
    fieldName: FieldNames
  }) => {
    setInputValue((prevValue) => ({
      ...prevValue,
      [fieldName]: value,
    }))
  }

  const handleInputChange = ({
    newInputValue,
    fieldName,
  }: {
    newInputValue: string
    fieldName: FieldNames
  }) => {
    const inputWithoutSpaces = newInputValue.replace(/ +/g, '')
    const withAllowedChars = inputWithoutSpaces.replace(
      /[^A-Z0-9,&_'+.-]/gi,
      '',
    )
    const options: string[] = withAllowedChars.split(',')

    if (options.length > 1) {
      const mergedOptions = value[fieldName].concat(
        options.map((option) => option.trim()).filter(Boolean),
      )

      handleSetValue({
        fieldName,
        value: mergedOptions,
      })

      handleSetInputValue({ fieldName, value: '' })
    } else {
      handleSetInputValue({ fieldName, value: withAllowedChars })
    }
  }

  const handleBlur = () => {
    /**
     * ⚠️ This picks up the unsaved text.
     */
    mapFilters.forEach(({ type }) => {
      if (inputValue[type]) {
        handleSetValue({
          fieldName: type,
          value: value[type].concat(inputValue[type]),
        })

        handleSetInputValue({ fieldName: type, value: '' })
      }
    })
  }

  return (
    <Dialog
      open={open}
      onClose={handelClose}
      aria-labelledby={t('filters.email.title')}
      PaperProps={dialogStyles}
      data-testid="email-dialog"
    >
      <DialogTitle sx={{ pt: 4 }}>{t('filters.email.title')}</DialogTitle>

      <DialogContent>
        <DialogContentText>
          <ChipInput
            value={value.emailContains}
            inputValue={inputValue.emailContains}
            handleOnChange={(newOptions: any) => {
              handleSetValue({
                fieldName: 'emailContains',
                value: newOptions,
              })
            }}
            handleInputChange={(newInputValue: string) =>
              handleInputChange({ fieldName: 'emailContains', newInputValue })
            }
            handleOnBlur={handleBlur}
            label={t('filters.email.options.contains')}
            helperText={t('filters.email.helperText')}
          />

          <ChipInput
            value={value.emailDoesNotContains}
            inputValue={inputValue.emailDoesNotContains}
            handleOnChange={(newOptions: any) => {
              handleSetValue({
                fieldName: 'emailDoesNotContains',
                value: newOptions,
              })
            }}
            handleInputChange={(newInputValue: string) =>
              handleInputChange({
                fieldName: 'emailDoesNotContains',
                newInputValue,
              })
            }
            handleOnBlur={handleBlur}
            label={t('filters.email.options.doesNotContain')}
            helperText={t('filters.email.helperText')}
            sx={{ mt: 4 }}
          />
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ p: 3.5, ...dialogActionsStyle }}>
        <Button
          onClick={handelClose}
          data-testid="email-btn-cancel"
          sx={{ mr: 1 }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          data-testid="email-btn-apply"
        >
          {t('filters.applyFilter')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FilterByEmail
